'use client'

import React, { memo, useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { ItemProps } from '@/components/Form/types'

import Button from '../../UiElements/Button'
import ButtonList from '@/components/UiElements/ListButton'
import ScrollToBottomButton from '@/components/UiElements/ScrollToBottomButton'
import Section2dForm from './Section2dForm'
import Splash from '@/components/splash/Splash'
import StickyDesignSection from './StickyDesignSection'
import Modal from '@/components/Modal/Modal'
import Alert from '@/components/UiElements/Alert'
import TwoDCanvas, { Section2D } from '../utils/TwoDCanvas'
import {
	clearSectionGeometry,
	hasCalibrationChanged,
	isValidPixelToSize,
	recalculateSectionGeometry,
} from '../utils/calibrationGeometry'

type CalibrationReason = 'initial' | 'recalibration' | 'image-change'

const formatGeometryValue = (value: number) => Number(value.toFixed(4)).toString()

const confirmCalibrationRecalculation = (
	previousPixelToSize: number,
	nextPixelToSize: number
) => new Promise<boolean>((resolve) => {
	Alert.showAlert({
		title: 'Recalculate Section Dimensions?',
		message: `Calibration will change from ${previousPixelToSize.toFixed(4)} to ${nextPixelToSize.toFixed(4)} mm/px. The section size and position will be recalculated to stay aligned with the image.`,
		button: 'Recalculate & Apply',
		cancel: 'Keep Current Calibration',
		isCancelable: true,
		callBack: resolve,
	})
})

const Preview2dPlugin: React.FC<ItemProps> = (props) => {

	const { formData } = props

	const canvasRef = useRef<HTMLCanvasElement>(null)
	const sectionFormScrollRef = useRef<HTMLDivElement>(null)
	const viewerRef = useRef<TwoDCanvas | null>(null)
	const prevSectionImageRef = useRef<string | null>(null)
	const sectionListRef = useRef<any[]>(formData.sections || [])
	const sectionSyncRequestIdRef = useRef(0)

	const [selectedSection, setSelectedSection] = React.useState<any | null>(null)
	const [sectionList, setSectionList] = React.useState<any[]>(formData.sections || [])
	const [fullScreen, setFullScreen] = React.useState<boolean>(false)
	const [loaded, setLoaded] = React.useState(false)
	const [openForm, setOpenForm] = React.useState<'section' | null>(null)

	// Drawing hint state
	const [drawingHint, setDrawingHint] = React.useState<string | null>(null)

	// Calibration state
	const [calibrating, setCalibrating] = React.useState(false)
	const [calibrationPixelLength, setCalibrationPixelLength] = React.useState<number | null>(null)
	const [showCalibrationInput, setShowCalibrationInput] = React.useState(false)
	const [calibrationMm, setCalibrationMm] = React.useState('')
	const [isApplyingCalibration, setIsApplyingCalibration] = React.useState(false)
	const preCalibrationSectionRef = useRef<any>(null) // stores section data before image change
	const calibrationReasonRef = useRef<CalibrationReason | null>(null)
	const calibrationRequestIdRef = useRef(0)
	const calibrationApplyPendingRef = useRef(false)
	const calibrationStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const getFileSrc = (file: any): string | null => {
		return file?.string || file?.url || null
	}

	const getSectionImageFile = (fd: any) => {
		return fd?.sectionImage || fd?.thumbnail || null
	}

	const getSectionImageSrc = (fd: any): string | null => {
		return getFileSrc(getSectionImageFile(fd))
	}

	const mapFormDataToSection2D = (fd: any): Section2D => ({
		sectionId: fd.sectionId,
		name: fd.name || '',
		offsetX: Number(fd.offsetX) || 0,
		offsetY: Number(fd.offsetY) || 0,
		width: Number(fd.width) || 0,
		height: Number(fd.height) || 0,
		imageUrl: getSectionImageSrc(fd) || undefined,
		shapeType: fd.shapeType?.value || fd.shapeType || 'rectangle',
		rotation: Number(fd.rotation) || 0,
		radius: fd.radius ? Number(fd.radius) : undefined,
		vertices: fd.vertices || undefined,
		materialType: fd.materialType?.value || fd.materialType || 'steel',
		materialFile: fd.materialSource?.mode === 'pattern'
			? getFileSrc(fd.materialSource?.file) || undefined
			: undefined,
		materialColor: fd.materialSource?.mode === 'pattern'
			? undefined
			: fd.materialSource?.color || undefined,
		engravingTint: fd.engravingTint != null ? Number(fd.engravingTint) : 0.5,
		overlayOpacity: fd.overlayOpacity != null ? Number(fd.overlayOpacity) : 1.0,
	})

	const getImageNaturalSize = (src: string): Promise<{ width: number; height: number }> => {
		return new Promise((resolve, reject) => {
			const img = new Image()
			img.onload = () => {
				resolve({
					width: img.naturalWidth || img.width,
					height: img.naturalHeight || img.height,
				})
			}
			img.onerror = reject
			img.src = src
		})
	}

	const isSameSize = (current: any, next: number) => {
		return Math.abs(Number(current) - next) < 0.1
	}

	const getTargetObjectSizeFromImage = (fd: any, imageSize: { width: number; height: number }) => {
		const pixelToSize = Number(fd.targetObject?.pixel_to_size)
		if (Number.isFinite(pixelToSize) && pixelToSize > 0) {
			return {
				width: Number((imageSize.width * pixelToSize).toFixed(4)),
				height: Number((imageSize.height * pixelToSize).toFixed(4)),
			}
		}

		return {
			width: imageSize.width,
			height: imageSize.height,
		}
	}

	const normalizeSectionsFor2D = async (sections: any[]) => {
		let changed = false

		const normalizedSections = await Promise.all((sections || []).map(async (section: any) => {
			const fd = section?.formData
			if (!fd) return section

			const imageFile = getSectionImageFile(fd)
			const imageSrc = getFileSrc(imageFile)
			if (!imageSrc) return section

			try {
				const imageSize = await getImageNaturalSize(imageSrc)
				const targetSize = getTargetObjectSizeFromImage(fd, imageSize)
				const shouldUpdateTargetObject =
					!isSameSize(fd.targetObject?.width, targetSize.width) ||
					!isSameSize(fd.targetObject?.height, targetSize.height)
				const shouldPromoteThumbnail = !!fd.thumbnail

				if (!shouldPromoteThumbnail && !shouldUpdateTargetObject) {
					return section
				}

				changed = true
				return {
					...section,
					formData: {
						...fd,
						sectionImage: fd.sectionImage || fd.thumbnail,
						thumbnail: null,
						targetObject: {
							...(fd.targetObject || {}),
							name: fd.targetObject?.name || '2d_surface',
							width: targetSize.width.toString(),
							height: targetSize.height.toString(),
						},
					},
				}
			} catch {
				if (!fd.thumbnail || fd.sectionImage) return section

				changed = true
				return {
					...section,
					formData: {
						...fd,
						sectionImage: fd.thumbnail,
						thumbnail: null,
					},
				}
			}
		}))

		return changed ? normalizedSections : sections
	}

	const syncSectionsToCanvas = (sections: any[]) => {
		if (!viewerRef.current) return
		const mapped = sections.map((s: any) => mapFormDataToSection2D(s.formData))
		const drawableSections = mapped.filter((section: Section2D) => section.width > 0 && section.height > 0)

		// Shapes without geometry must not enter the drawable list, but their
		// section images still need to stay cached after deleting an overlay.
		viewerRef.current.setSections(drawableSections, mapped)
	}

	// Keep sectionListRef in sync
	useEffect(() => { sectionListRef.current = sectionList }, [sectionList])

	// Handle canvas-driven section updates (move/resize/rotate)
	const handleCanvasUpdateSection = React.useCallback((updated: Section2D) => {
		const currentList = sectionListRef.current
		const newList = currentList.map((s: any) => {
			if (s.formData.sectionId !== updated.sectionId) return s
			return {
				...s,
				formData: {
					...s.formData,
					offsetX: formatGeometryValue(updated.offsetX),
					offsetY: formatGeometryValue(updated.offsetY),
					width: formatGeometryValue(updated.width),
					height: formatGeometryValue(updated.height),
					rotation: updated.rotation.toString(),
					radius: updated.radius != null ? formatGeometryValue(updated.radius) : s.formData.radius,
					vertices: updated.vertices || s.formData.vertices,
				},
			}
		})
		sectionSyncRequestIdRef.current += 1
		sectionListRef.current = newList
		setSectionList(newList)
		props?.onChange?.('sections', newList)
		syncSectionsToCanvas(newList)

		// Update selectedSection so the form sliders reflect the change
		const updatedEntry = newList.find((s: any) => s.formData.sectionId === updated.sectionId)
		if (updatedEntry) {
			setSelectedSection(updatedEntry.formData)
		}
	}, [])

	// Initialize 2D canvas
	useEffect(() => {
		if (canvasRef.current && !viewerRef.current) {
			viewerRef.current = new TwoDCanvas(canvasRef.current)
			viewerRef.current.setOnSectionChange(handleCanvasUpdateSection)
			viewerRef.current.setOnDrawEnd(() => setDrawingHint(null))
		}
		return () => {
			calibrationRequestIdRef.current += 1
			if (calibrationStartTimerRef.current !== null) {
				clearTimeout(calibrationStartTimerRef.current)
				calibrationStartTimerRef.current = null
			}
			viewerRef.current?.dispose()
			viewerRef.current = null
		}
	}, [])


	// Set up target object and optionally load product image
	useEffect(() => {
		const tw = Number(formData?.targetObjectWidth) || 200
		const th = Number(formData?.targetObjectHeight) || 200

		if (viewerRef.current) {
			viewerRef.current.setTargetObject(tw, th)

			const imageStr = formData?.productImage?.string
			const onReady = () => {
				viewerRef.current?.fitImageToView()
				setLoaded(true)

				// Load existing sections
				if (formData?.sections?.length > 0) {
					syncSectionsToCanvas(formData.sections)
					const first = formData.sections[0]?.formData
					if (first) {
						setSelectedSection(first)
						setOpenForm('section')
						viewerRef.current?.setSelectedSection(first.sectionId)
						prevSectionImageRef.current = getSectionImageSrc(first)

						// Set target object from first section's calibrated dimensions
						const to = first.targetObject
						if (to?.width && to?.height) {
							viewerRef.current?.setTargetObject(Number(to.width), Number(to.height))
						}
					}
				}
			}

			if (imageStr) {
				viewerRef.current.loadProductImage(imageStr).then(onReady)
			} else {
				onReady()
			}
		}
	}, [formData?.productImage?.string, formData?.targetObjectWidth, formData?.targetObjectHeight])


	// Sync sections to canvas when list changes
	useEffect(() => {
		if (loaded) {
			syncSectionsToCanvas(sectionList)

			// If selected section no longer exists in the list (e.g., IDs changed after save),
			// re-select the first section so the canvas renders it
			if (selectedSection && sectionList.length > 0) {
				const exists = sectionList.some((s: any) => s.formData?.sectionId === selectedSection.sectionId)
				if (!exists) {
					const first = sectionList[0]?.formData
					if (first) {
						setSelectedSection(first)
						setOpenForm('section')
						viewerRef.current?.setSelectedSection(first.sectionId)
						prevSectionImageRef.current = getSectionImageSrc(first)

						const to = first.targetObject
						if (to?.width && to?.height) {
							viewerRef.current?.setTargetObject(Number(to.width), Number(to.height))
							viewerRef.current?.fitImageToView()
						}
					}
				}
			}
		}
	}, [sectionList, loaded])


	// Sync from parent form data
	useEffect(() => {
		let isCancelled = false
		const requestId = ++sectionSyncRequestIdRef.current

		const syncFormSections = async () => {
			const sections = formData.sections || []
			const normalizedSections = await normalizeSectionsFor2D(sections)
			if (isCancelled || requestId !== sectionSyncRequestIdRef.current) return

			sectionListRef.current = normalizedSections
			setSectionList(normalizedSections)
			if (normalizedSections !== sections) {
				props?.onChange?.('sections', normalizedSections)
				const selectedId = selectedSection?.sectionId
				const normalizedSelected = selectedId
					? normalizedSections.find((section: any) => section.formData?.sectionId === selectedId)?.formData
					: normalizedSections[0]?.formData

				if (normalizedSelected) {
					setSelectedSection(normalizedSelected)
					prevSectionImageRef.current = getSectionImageSrc(normalizedSelected)
					viewerRef.current?.setSelectedSection(normalizedSelected.sectionId)

					const targetObject = normalizedSelected.targetObject
					if (targetObject?.width && targetObject?.height) {
						viewerRef.current?.setTargetObject(Number(targetObject.width), Number(targetObject.height))
						viewerRef.current?.fitImageToView()
					}
				}
			}
		}

		syncFormSections()

		return () => {
			isCancelled = true
		}
	}, [formData.sections, loaded])


	const clearScheduledCalibrationStart = () => {
		if (calibrationStartTimerRef.current !== null) {
			clearTimeout(calibrationStartTimerRef.current)
			calibrationStartTimerRef.current = null
		}
	}

	const resetCalibrationUi = () => {
		calibrationRequestIdRef.current += 1
		calibrationApplyPendingRef.current = false
		clearScheduledCalibrationStart()
		viewerRef.current?.cancelCalibration()
		setCalibrating(false)
		setCalibrationPixelLength(null)
		setShowCalibrationInput(false)
		setCalibrationMm('')
		setIsApplyingCalibration(false)
		calibrationReasonRef.current = null
	}

	const startCalibrationMode = (imagePixelsPerCanvasUnit = 1) => {
		if (!viewerRef.current) return

		calibrationApplyPendingRef.current = false
		setCalibrationPixelLength(null)
		setShowCalibrationInput(false)
		setCalibrationMm('')
		setIsApplyingCalibration(false)
		setCalibrating(true)
		viewerRef.current.startCalibration((imagePixelLength: number) => {
			setCalibrationPixelLength(imagePixelLength)
			setShowCalibrationInput(true)
		}, imagePixelsPerCanvasUnit)
	}

	const scheduleCalibrationStart = (
		imagePixelsPerCanvasUnit: number,
		requestId: number
	) => {
		clearScheduledCalibrationStart()
		calibrationStartTimerRef.current = setTimeout(() => {
			calibrationStartTimerRef.current = null
			if (requestId !== calibrationRequestIdRef.current) return
			startCalibrationMode(imagePixelsPerCanvasUnit)
		}, 300)
	}

	const handleStartCalibrationForSection = async (section = selectedSection) => {
		if (!section) return

		const imageUrl = getSectionImageSrc(section)
		if (!imageUrl) return

		if (viewerRef.current?.isDrawing()) {
			viewerRef.current.cancelDrawing()
		}

		setDrawingHint(null)
		clearScheduledCalibrationStart()
		const requestId = ++calibrationRequestIdRef.current
		setSelectedSection(section)
		viewerRef.current?.setSelectedSection(section.sectionId)
		preCalibrationSectionRef.current = section
		calibrationReasonRef.current = isValidPixelToSize(section.targetObject?.pixel_to_size)
			? 'recalibration'
			: 'initial'

		const targetObject = section.targetObject
		let baseWidth = Number(targetObject?.width) || 0
		let imageSize: { width: number; height: number } | null = null

		try {
			imageSize = await getImageNaturalSize(imageUrl)
		} catch {
			imageSize = null
		}

		if (requestId !== calibrationRequestIdRef.current) return

		if (targetObject?.width && targetObject?.height) {
			viewerRef.current?.setTargetObject(Number(targetObject.width), Number(targetObject.height))
			viewerRef.current?.fitImageToView()
		} else {
			if (imageSize) {
				baseWidth = imageSize.width
				viewerRef.current?.setTargetObject(imageSize.width, imageSize.height)
				viewerRef.current?.fitImageToView()
			} else {
				baseWidth = 200
			}
		}

		const canvasBaseWidth = viewerRef.current?.getCanvasBaseSize().width || baseWidth
		const imagePixelsPerCanvasUnit = imageSize && canvasBaseWidth > 0
			? imageSize.width / canvasBaseWidth
			: 1
		startCalibrationMode(imagePixelsPerCanvasUnit)
	}

	const handleAddSection = () => {
		const newSection: any = {
			sectionId: uuidv4(),
			name: `Section_${(sectionList?.length || 0) + 1}`,
			targetObject: { name: '2d_surface', width: '200', height: '200' },
			width: '50',
			height: '50',
			offsetX: '0',
			offsetY: '100',
			radialAngle: '0',
			centerRadialAngle: '0',
			shapeType: { value: 'rectangle', label: 'Rectangle' },
			rotation: '0',
			materialType: { value: 'steel', label: 'Steel / Metal' },
			materialSource: null,
			engravingTint: 0.5,
			overlayOpacity: 1.0,
		}

		const updatedSections = [...(sectionListRef.current || []), {
			formData: newSection,
			validations: {},
		}]

		sectionSyncRequestIdRef.current += 1
		sectionListRef.current = updatedSections
		setSectionList(updatedSections)
		props?.onChange?.('sections', updatedSections)
		syncSectionsToCanvas(updatedSections)
		setSelectedSection(newSection)
		setOpenForm('section')
		prevSectionImageRef.current = null

		viewerRef.current?.setSelectedSection(newSection.sectionId)
		startDrawingMode('rectangle', newSection.sectionId)
	}

	const handleSelectSection = (sectionId: string) => {
		const section = sectionList.find((s) => s.formData?.sectionId === sectionId)
		if (!section) return

		setSelectedSection(section.formData)
		setOpenForm('section')
		viewerRef.current?.setSelectedSection(sectionId)
		prevSectionImageRef.current = getSectionImageSrc(section.formData)

		// Update canvas target object from this section's calibrated dimensions
		const to = section.formData?.targetObject
		if (to?.width && to?.height) {
			viewerRef.current?.setTargetObject(Number(to.width), Number(to.height))
			viewerRef.current?.fitImageToView()
		}
	}

	const handleCloseForm = () => {
		setOpenForm(null)
		setSelectedSection(null)
		viewerRef.current?.setSelectedSection(null)
		prevSectionImageRef.current = null
	}

	const handleUpdateSections = async ({
		formData: sectionFormData,
		validations,
		isDeleted,
		isShapeDeleted,
	}: {
		formData: any
		validations: any
		isDeleted?: boolean
		isShapeDeleted?: boolean
	}) => {
		if (!selectedSection) return

		let updatedSections = sectionListRef.current
		let nextSectionData = { ...sectionFormData, thumbnail: null }
		if (!isDeleted) {
			if (isShapeDeleted) {
				viewerRef.current?.cancelDrawing()
				setDrawingHint(null)
			}

			// Detect shape type change → cancel current drawing and start new one
			const prevShapeType = selectedSection?.shapeType?.value || selectedSection?.shapeType || 'rectangle'
			const newShapeType = nextSectionData.shapeType?.value || nextSectionData.shapeType || 'rectangle'
			if (prevShapeType !== newShapeType) {
				if (viewerRef.current?.isDrawing()) {
					viewerRef.current.cancelDrawing()
				}
				startDrawingMode(newShapeType, nextSectionData.sectionId)
			}

			// Detect new/changed section image → auto-enter calibration mode
			const newImageUrl = getSectionImageSrc(nextSectionData)
			const prevImageUrl = prevSectionImageRef.current
			if (newImageUrl && newImageUrl !== prevImageUrl) {
				const requestId = ++calibrationRequestIdRef.current
				clearScheduledCalibrationStart()
				// Save the previous section state before the image change (for cancel/restore)
				preCalibrationSectionRef.current = selectedSection
				calibrationReasonRef.current = 'image-change'
				prevSectionImageRef.current = newImageUrl

				try {
					const imageSize = await getImageNaturalSize(newImageUrl)
					if (requestId !== calibrationRequestIdRef.current) return

					const targetObject = {
						...(nextSectionData.targetObject || {}),
						name: nextSectionData.targetObject?.name || '2d_surface',
						width: imageSize.width.toString(),
						height: imageSize.height.toString(),
					}
					delete (targetObject as any).pixel_to_size

					nextSectionData = {
						...nextSectionData,
						targetObject,
					}

					viewerRef.current?.setTargetObject(imageSize.width, imageSize.height)
					viewerRef.current?.fitImageToView()

					// Wait for canvas to render the new image, then start calibration
					const canvasBaseWidth = viewerRef.current?.getCanvasBaseSize().width || imageSize.width
					const imagePixelsPerCanvasUnit = imageSize.width / canvasBaseWidth
					scheduleCalibrationStart(imagePixelsPerCanvasUnit, requestId)
				} catch {
					if (requestId !== calibrationRequestIdRef.current) return
					scheduleCalibrationStart(1, requestId)
				}
			} else if (!newImageUrl && prevImageUrl) {
				// Image was removed — reset section drawing and clear ref
				resetCalibrationUi()
				preCalibrationSectionRef.current = null
				prevSectionImageRef.current = null

				if (viewerRef.current?.isDrawing()) {
					viewerRef.current.cancelDrawing()
				}

				// Geometry belongs to the removed image and must not carry over.
				nextSectionData = {
					...clearSectionGeometry(nextSectionData),
					thumbnail: null,
				}
			}

			// Keep selectedSection in sync with latest form data
			setSelectedSection(nextSectionData)

			updatedSections = sectionListRef.current.map((section) =>
				section.formData.sectionId === nextSectionData.sectionId
					? { formData: nextSectionData, validations }
					: section
			)
		} else {
			updatedSections = updatedSections.filter((s) => s.formData.sectionId !== nextSectionData.sectionId)
			setOpenForm(null)
			setSelectedSection(null)
			viewerRef.current?.setSelectedSection(null)
			prevSectionImageRef.current = null
		}

		sectionSyncRequestIdRef.current += 1
		sectionListRef.current = updatedSections
		setSectionList(updatedSections)
		props?.onChange?.('sections', updatedSections)

		// Update canvas
		syncSectionsToCanvas(updatedSections)
		if (!isDeleted) {
			viewerRef.current?.setSelectedSection(nextSectionData.sectionId)

			if (isShapeDeleted) {
				const shapeType = nextSectionData.shapeType?.value || nextSectionData.shapeType || 'rectangle'
				startDrawingMode(shapeType, nextSectionData.sectionId)
			}
		}
	}

	const handleFullScreen = () => {
		const el = document.getElementById('section-2d-plugin')
		if (!el) return
		if (fullScreen) {
			el.removeAttribute('style')
			setFullScreen(false)
		} else {
			el.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;z-index:9999;'
			setFullScreen(true)
		}
		// Trigger canvas resize
		setTimeout(() => viewerRef.current?.fitImageToView(), 100)
	}

	// --- Drawing handler ---

	const drawingHints: Record<string, string> = {
		rectangle: 'Click and drag to draw a rectangle. Hold Space to show magnifier.',
		circle: 'Click and drag from center outward to draw a circle. Hold Space to show magnifier.',
		polygon: 'Click to add points. Double-click or press Enter to close. Hold Space to show magnifier.',
	}

	const startDrawingMode = (shapeType: string, sectionId: string) => {
		if (!viewerRef.current) return

		setDrawingHint(drawingHints[shapeType] || null)
		const drawMode = `draw-${shapeType}` as 'draw-rectangle' | 'draw-circle' | 'draw-polygon'

		viewerRef.current.startDrawing(drawMode, (result) => {
			// Update the section with the drawn shape data
			const currentList = sectionListRef.current

			const newList = currentList.map((s: any) => {
				if (s.formData.sectionId !== sectionId) return s
				return {
					...s,
					formData: {
						...s.formData,
						shapeType: { value: result.shapeType, label: result.shapeType.charAt(0).toUpperCase() + result.shapeType.slice(1) },
						offsetX: formatGeometryValue(result.offsetX),
						offsetY: formatGeometryValue(result.offsetY),
						width: formatGeometryValue(result.width),
						height: formatGeometryValue(result.height),
						rotation: '0',
						radius: result.radius != null ? formatGeometryValue(result.radius) : s.formData.radius,
						vertices: result.vertices || null,
					},
				}
			})

			sectionSyncRequestIdRef.current += 1
			sectionListRef.current = newList
			setSectionList(newList)
			props?.onChange?.('sections', newList)
			syncSectionsToCanvas(newList)

			const updatedEntry = newList.find((s: any) => s.formData.sectionId === sectionId)
			if (updatedEntry) {
				setSelectedSection(updatedEntry.formData)
			}
		})
	}

	// --- Calibration handlers ---

	const handleCancelCalibration = () => {
		// Restore previous section state (undo the image change)
		const prevSection = preCalibrationSectionRef.current
		resetCalibrationUi()

		if (prevSection) {
			setSelectedSection(prevSection)
			prevSectionImageRef.current = getSectionImageSrc(prevSection)

			const updatedSections = sectionListRef.current.map((section) =>
				section.formData.sectionId === prevSection.sectionId
					? { ...section, formData: prevSection }
					: section
			)
			sectionSyncRequestIdRef.current += 1
			sectionListRef.current = updatedSections
			setSectionList(updatedSections)
			props?.onChange?.('sections', updatedSections)
			syncSectionsToCanvas(updatedSections)

			// Restore canvas target object
			const to = prevSection.targetObject
			if (to?.width && to?.height) {
				viewerRef.current?.setTargetObject(Number(to.width), Number(to.height))
				viewerRef.current?.fitImageToView()
			}
		}
		preCalibrationSectionRef.current = null
	}

	const handleConfirmCalibration = async () => {
		if (
			calibrationApplyPendingRef.current ||
			isApplyingCalibration ||
			!calibrationPixelLength ||
			!calibrationMm ||
			!selectedSection
		) return

		const mmValue = Number(calibrationMm)
		if (mmValue <= 0 || !isFinite(mmValue)) return

		calibrationApplyPendingRef.current = true
		setIsApplyingCalibration(true)
		const requestId = calibrationRequestIdRef.current
		const nextPixelToSize = mmValue / calibrationPixelLength
		const previousSection = preCalibrationSectionRef.current || selectedSection
		const previousPixelToSize = Number(previousSection.targetObject?.pixel_to_size)
		const shouldRecalculateGeometry =
			calibrationReasonRef.current === 'recalibration' &&
			isValidPixelToSize(previousPixelToSize)

		if (
			shouldRecalculateGeometry &&
			hasCalibrationChanged(previousPixelToSize, nextPixelToSize)
		) {
			const confirmed = await confirmCalibrationRecalculation(
				previousPixelToSize,
				nextPixelToSize
			)

			if (requestId !== calibrationRequestIdRef.current) return

			if (!confirmed) {
				handleCancelCalibration()
				return
			}
		}

		const sectionImageUrl = getSectionImageSrc(selectedSection)
		if (!sectionImageUrl) {
			handleCancelCalibration()
			return
		}

		let imageSize: { width: number; height: number }
		try {
			imageSize = await getImageNaturalSize(sectionImageUrl)
		} catch {
			if (requestId === calibrationRequestIdRef.current) {
				handleCancelCalibration()
			}
			return
		}

		if (requestId !== calibrationRequestIdRef.current) return

		const targetWidth = Number((imageSize.width * nextPixelToSize).toFixed(4))
		const targetHeight = Number((imageSize.height * nextPixelToSize).toFixed(4))
		const geometrySection = shouldRecalculateGeometry
			? recalculateSectionGeometry(previousSection, previousPixelToSize, nextPixelToSize)
			: clearSectionGeometry(selectedSection)
		const updatedSectionData: Record<string, any> = {
			...geometrySection,
			thumbnail: null,
			targetObject: {
				...selectedSection.targetObject,
				width: targetWidth.toString(),
				height: targetHeight.toString(),
				pixel_to_size: nextPixelToSize,
			},
		}

		setSelectedSection(updatedSectionData)

		const updatedSections = sectionListRef.current.map((section) =>
			section.formData.sectionId === updatedSectionData.sectionId
				? { ...section, formData: updatedSectionData }
				: section
		)
		sectionSyncRequestIdRef.current += 1
		sectionListRef.current = updatedSections
		setSectionList(updatedSections)
		props?.onChange?.('sections', updatedSections)

		viewerRef.current?.setTargetObject(targetWidth, targetHeight)
		viewerRef.current?.fitImageToView()
		syncSectionsToCanvas(updatedSections)

		resetCalibrationUi()
		preCalibrationSectionRef.current = null

		if (!shouldRecalculateGeometry) {
			const shapeType = updatedSectionData.shapeType?.value || updatedSectionData.shapeType || 'rectangle'
			startDrawingMode(shapeType, updatedSectionData.sectionId)
		}
	}


	const designSection = (
		<div className="w-full rounded-md relative bg-gray-100 shadow border border-gray-300 p-0 flex-row flex h-[calc(100vh-100px)] overflow-hidden" id={'section-2d-plugin'}>
			<Splash text={'Loading 2D Preview...'} className={'opacity-100'} isOpen={!loaded} />

			{/* Calibration lockdown overlay — covers everything except the canvas */}
			{calibrating && !showCalibrationInput && (
				<div className="absolute inset-0 z-[10] pointer-events-none">
					{/* Keep disabled panels aligned with the editor's light surface. */}
					<div className="absolute top-0 bottom-[116px] left-0 w-[132px] bg-gray-100 bg-opacity-[0.8] pointer-events-auto" />
					<div className="absolute top-0 right-0 w-[382px] h-full bg-gray-100 bg-opacity-[0.8] pointer-events-auto" />
					{/* Instruction label on canvas */}
					<div className="absolute top-3 left-[132px] right-[382px] flex justify-center pointer-events-none">
						<span className="bg-red-500 text-white text-text-md font-semibold px-lg py-sm rounded-md shadow-lg">
							Draw a horizontal line on the image to calibrate
						</span>
					</div>
				</div>
			)}

			{/* Left panel: section list */}
			<div className="flex !item-center flex-col gap-lg max-w-[132px] w-max z-[1] p-md">
				<div className={`flex w-[100px] ${sectionList?.length > 0 ? 'justify-center' : ' justify-start'}`}>
					<Button onClick={handleAddSection} className="!rounded-full font-icon-solid w-[44px] h-[44px]">+</Button>
				</div>
				<div className="flex justify-center inset-1 flex-1">
					{sectionList?.length > 0 && (
						<div className={'flex flex-col gap-sm '}>
							<ButtonList
								className={(selected: boolean, item: any) =>
									`w-[100px] h-[100px] bg-white rounded-md border border-gray-200 justify-center items-center overflow-hidden hover:border-primary-500 active:shadow-focus
                                    ${selected ? `shadow-xl border-primary-500  border-[2px] ` : ''}
                                    ${item?.validation > 0 ? 'border-red-500 hover:border-red-500 active:shadow-red' : ''}
                                    `
								}
								items={sectionList?.map((section: any) => {
									const item = section.formData
									const validations = Object.values(section.validations || {})

									return ({
										...item,
										value: item.sectionId,
										label: (
											<div className={'flex justify-center items-center h-[100%] w-[100%] relative p-xs'}>
												{validations?.length > 0 && (
													<div
														className={'bg-red-500 text-white text-[11px] absolute bottom-0 left-1/2 -translate-x-1/2  w-[100%] flex justify-center items-center'}
													>
														{validations?.length}
													</div>
												)}
												{getSectionImageSrc(item)
													? <img src={getSectionImageSrc(item) || ''} className={'object-contain h-[100%]'} alt={item.name} />
													: <span className={'flex flex-col '}>
														<span className={'font-icon-regular text-[30px] text-gray-300'}></span>
														<span className={'text-title-sm text-gray-500'}>{item.name}</span>
													</span>
												}
											</div>
										),
										validation: validations.length,
									})
								})}
								selected={selectedSection?.sectionId}
								onClick={(val: string) => {
									handleSelectSection(val)
								}}
							/>
						</div>
					)}
				</div>
				<div className={`flex flex-col gap-md relative z-[20] ${sectionList?.length > 0 ? 'items-center' : ' items-start'}`}>
					{selectedSection && getSectionImageSrc(selectedSection) && (
						<Button
							onClick={() => {
								if (calibrating) {
									handleCancelCalibration()
									return
								}
								handleStartCalibrationForSection()
							}}
							type={'alt'}
							className={`!rounded-full font-icon-solid w-[44px] h-[44px] ${
								calibrating ? '!bg-gray-900 !border-gray-900 !text-white hover:!bg-black hover:!border-black' : ''
							}`}
						>
							{calibrating ? '' : ''}
						</Button>
					)}
					<Button onClick={handleFullScreen} type={'alt'} className="!rounded-full font-icon-solid w-[44px] h-[44px] ">{fullScreen ? '' : ''}</Button>
				</div>
			</div>

			{/* Center panel: 2D canvas */}
			<div className="flex flex-1 z-[1] overflow-hidden relative">
				{/* Drawing mode hint */}
				{drawingHint && (
					<div className="absolute top-3 left-1/2 -translate-x-1/2 z-[2]">
						<span className="bg-gray-700/80 text-white text-text-sm px-lg py-sm rounded-md shadow-lg">
							{drawingHint}
						</span>
					</div>
				)}
				{/* Calibration info label — click to re-calibrate */}
				{!drawingHint && selectedSection?.targetObject?.pixel_to_size && !calibrating && (
					<div className="absolute top-2 left-1/2 -translate-x-1/2 z-[2]">
						<button
							className="text-text-sm text-gray-500 bg-white/80 rounded px-sm py-xs shadow hover:bg-white hover:text-primary-600 hover:shadow-md transition-all cursor-pointer"
							title="Click to re-calibrate"
							onClick={() => handleStartCalibrationForSection()}
						>
							Calibrated: {Number(selectedSection.targetObject.pixel_to_size).toFixed(4)} mm/px &#9998;
						</button>
					</div>
				)}
				<canvas
					ref={canvasRef}
					style={{ width: '100%', height: '100%', outline: 'none' }}
				/>
			</div>

			{/* Right panel: Section form */}
			<div className="z-[1] relative !w-[382px] h-full shrink-0 rounded-md">
				<div
					ref={sectionFormScrollRef}
					className="relative w-full h-full justify-end pb-md rounded-md p-md block"
					style={{ overflowY: 'hidden', overflowX: 'hidden' }}
				>
					<Section2dForm
						profileType={formData?.profileType?.value || formData?.profileType}
						isOpen={openForm === 'section'}
						close={handleCloseForm}
						onUpdate={handleUpdateSections}
						sectionData={selectedSection}
						sectionList={sectionList}
					/>
				</div>
				<ScrollToBottomButton containerRef={sectionFormScrollRef} />
			</div>

			{/* Calibration mm input modal — only mount when needed to avoid Modal's onClose firing on isOpen=false */}
			{showCalibrationInput && <Modal
				isOpen={true}
				onClose={handleCancelCalibration}
				title="Enter Calibration Length"
			>
				<div className="flex flex-col gap-lg w-[350px]">
					<p className="text-text-md text-gray-600">
						The line you drew is <strong>{calibrationPixelLength?.toFixed(0)}</strong> pixels long.
						Enter its real-world length in millimeters:
					</p>
					<div className="flex flex-row gap-sm items-center">
						<input
							type="number"
							min="0.1"
							step="0.1"
							value={calibrationMm}
							onChange={(e) => setCalibrationMm(e.target.value)}
							placeholder="Length in mm"
							autoFocus
							className="transition-all outline-none w-full text-text-md text-gray-900 rounded-md border border-gray-300 bg-white placeholder:text-gray-400 focus:shadow-focus focus:border-primary-500 hover:border-primary-500 shadow-xs h-[44px] px-md"
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleConfirmCalibration()
							}}
						/>
						<span className="text-text-md text-gray-500">mm</span>
					</div>
					<div className="flex flex-row gap-md justify-end">
						<Button type="alt" onClick={handleCancelCalibration}>Cancel</Button>
						<Button
							onClick={handleConfirmCalibration}
							disabled={isApplyingCalibration || !calibrationMm || Number(calibrationMm) <= 0}
						>
							{isApplyingCalibration ? 'Applying...' : 'Apply'}
						</Button>
					</div>
				</div>
			</Modal>}
		</div>
	)

	return (
		<StickyDesignSection scrollContainerRef={sectionFormScrollRef} disabled={fullScreen}>
			{designSection}
		</StickyDesignSection>
	)
}

export default memo(Preview2dPlugin)
